import React from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup, InputGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put } from '@app/utils/httpProvider';
import { LOPDAOTAOTHUCHANH_API, DOITUONG_API } from '@app/utils/apiUrl';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as moment from 'moment';
import { 
    messageError, 
    messageToast, 
    status200,
    trangThaiDotTuyenSinhValue,
    objectTrangThaiDotTuyenSinh,
    loaiTuyenSinhValue,
    objectLoaiTuyenSinh,
    objectDoiTuong,
    doiTuongValue,
    styleSelectDefault,
    styleSelectError
} from '../../../../../utils/define';
import { URI_INTERNAL } from '../../../../../utils/pathLocations';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import Select from 'react-select';
import Editor from '../../../../../components/editor/Editor';
import DoiTuongMienGiamFields, { parseDoiTuongMienGiam, stringifyDoiTuongMienGiam } from '../dotTuyenSinh/DoiTuongMienGiamFields';
import DocumentInput from '../../../../../components/document-input/DocumentInput';

const schema = yup.object().shape({
    tenKhoaHoc: yup.string().required(messageError.emptyMessage).max(255, 'Tên khóa học không được vượt quá 255 ký tự'),
    loaiTuyenSinh: yup.string().required(messageError.emptyMessage),
    noiDungKhoaHoc: yup.string().nullable(),
    ngayKhaiGiang: yup.string().required(messageError.emptyMessage),
    ngayBatDauNhanHoSo: yup.date().nullable(),
    ngayKetThucNhanHoSo: yup.date().nullable(),
    doiTuongEnum: yup.string().required(messageError.emptyMessage),
    chucDanhIds: yup.array().nullable(),
    thoiGianBacSi: yup.number().min(0, 'Thời gian không được nhỏ hơn 0').nullable(),
    hocPhiBacSi: yup.number().min(0, 'Học phí không được nhỏ hơn 0').nullable(),
    thoiGianYSi: yup.number().min(0, 'Thời gian không được nhỏ hơn 0').nullable(),
    hocPhiYSi: yup.number().min(0, 'Học phí không được nhỏ hơn 0').nullable(),
    thoiGianDieuDuong: yup.number().min(0, 'Thời gian không được nhỏ hơn 0').nullable(),
    hocPhiDieuDuong: yup.number().min(0, 'Học phí không được nhỏ hơn 0').nullable(),
    thoiGianKTV: yup.number().min(0, 'Thời gian không được nhỏ hơn 0').nullable(),
    hocPhiKTV: yup.number().min(0, 'Học phí không được nhỏ hơn 0').nullable(),
    thoiGianDinhDuongLamSang: yup.number().min(0, 'Thời gian không được nhỏ hơn 0').nullable(),
    hocPhiDinhDuongLamSang: yup.number().min(0, 'Học phí không được nhỏ hơn 0').nullable(),
    trangThaiDotTuyenSinh: yup.string().required(messageError.emptyMessage),
    doiTuongMienGiams: yup.array().when('coDoiTuongMienGiam', {
        is: true,
        then: yup.array().of(yup.object().shape({ tenDoiTuong: yup.string().trim().required('Vui lòng nhập tên đối tượng').max(255, 'Tên đối tượng không được vượt quá 255 ký tự') })).min(1, 'Vui lòng thêm ít nhất một đối tượng')
    }),
});

const LopDaoTaoThucHanhEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const params = new URLSearchParams(location.search.substring(1));
    const itemId = params.get('id');

    const itemQuery = useQuery(['lopDaoTaoThucHanhData', itemId], () => Get(LOPDAOTAOTHUCHANH_API.GET_ITEM, itemId), {
        enabled: !!itemId
    });

    const { data: doiTuongData } = useQuery(
        ['doiTuongDataLopThucHanh'],
        () => Get(DOITUONG_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }),
        { keepPreviousData: true }
    );
    const listDoiTuong = doiTuongData?.data?.$values || doiTuongData?.data || [];

    const editMutation = useMutation((value) => Put(LOPDAOTAOTHUCHANH_API.UPDATE_ITEM, itemId, value));

    const handleEdit = (values) => {
        const { coDoiTuongMienGiam, doiTuongMienGiams, ...requestValues } = values;
        const selectedChucDanhs = listDoiTuong
            .filter(x => values.chucDanhIds && values.chucDanhIds.includes(x.id))
            .map(x => (x.tenDoiTuong || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/đ/g, 'd')
                .replace(/Đ/g, 'D')
                .trim()
                .toLowerCase());

        const isBacSi = selectedChucDanhs.includes('bac si');
        const isYSi = selectedChucDanhs.includes('y si');
        const isDieuDuong = selectedChucDanhs.includes('dieu duong');
        const isKTV = selectedChucDanhs.includes('ky thuat vien');
        const isDinhDuongLamSang = selectedChucDanhs.includes('dinh duong lam sang');

        const model = {
            ...requestValues,
            loaiTuyenSinh: values.loaiTuyenSinh,
            ngayKhaiGiang: values.ngayKhaiGiang || null,
            ngayBatDauNhanHoSo: values.ngayBatDauNhanHoSo || null,
            ngayKetThucNhanHoSo: values.ngayKetThucNhanHoSo || null,
            chucDanhIds: values.chucDanhIds && values.chucDanhIds.length > 0 ? values.chucDanhIds.join(',') : null,
            thoiGianBacSi: isBacSi ? (values.thoiGianBacSi === '' ? null : values.thoiGianBacSi) : null,
            hocPhiBacSi: isBacSi ? (values.hocPhiBacSi === '' ? null : values.hocPhiBacSi) : null,
            thoiGianYSi: isYSi ? (values.thoiGianYSi === '' ? null : values.thoiGianYSi) : null,
            hocPhiYSi: isYSi ? (values.hocPhiYSi === '' ? null : values.hocPhiYSi) : null,
            thoiGianDieuDuong: isDieuDuong ? (values.thoiGianDieuDuong === '' ? null : values.thoiGianDieuDuong) : null,
            hocPhiDieuDuong: isDieuDuong ? (values.hocPhiDieuDuong === '' ? null : values.hocPhiDieuDuong) : null,
            thoiGianKTV: isKTV ? (values.thoiGianKTV === '' ? null : values.thoiGianKTV) : null,
            hocPhiKTV: isKTV ? (values.hocPhiKTV === '' ? null : values.hocPhiKTV) : null,
            thoiGianDinhDuongLamSang: isDinhDuongLamSang ? (values.thoiGianDinhDuongLamSang === '' ? null : values.thoiGianDinhDuongLamSang) : null,
            hocPhiDinhDuongLamSang: isDinhDuongLamSang ? (values.hocPhiDinhDuongLamSang === '' ? null : values.hocPhiDinhDuongLamSang) : null,
            doiTuongMienGiam: coDoiTuongMienGiam ? stringifyDoiTuongMienGiam(doiTuongMienGiams) : '',
            fileDinhKem: values.fileDinhKem || null,
        };

        editMutation.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.setQueryData(['lopDaoTaoThucHanhData', itemId], model);
                    history.push(URI_INTERNAL.LOPDAOTAOTHUCHANH_PATH);
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    if (itemQuery.isLoading) {
        return <div>Đang tải....</div>;
    }

    if (itemQuery.isError || !itemQuery.data) {
        return <div>Có lỗi xảy ra hoặc không tìm thấy dữ liệu</div>;
    }

    const currentData = itemQuery.data;

    const initialTrangThai = currentData.trangThaiDotTuyenSinh;
    const initialDoiTuongEnum = currentData.doiTuongEnum;
    const initialLoai = currentData.loaiTuyenSinh;
    const initialDoiTuongMienGiams = parseDoiTuongMienGiam(currentData.doiTuongMienGiam ?? '');

    return (
        <Card>
            <Card.Header as="h5">Sửa Lớp Đào tạo thực hành</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleEdit(values)}
                    initialValues={{
                        tenKhoaHoc: currentData.tenKhoaHoc ?? '',
                        loaiTuyenSinh: initialLoai ?? loaiTuyenSinhValue.tuyenSinhDTTH,
                        noiDungKhoaHoc: currentData.noiDungKhoaHoc ?? '',
                        ngayKhaiGiang: currentData.ngayKhaiGiang ? moment(currentData.ngayKhaiGiang).format('YYYY-MM-DD') : '',
                        ngayBatDauNhanHoSo: currentData.ngayBatDauNhanHoSo ? moment(currentData.ngayBatDauNhanHoSo).format('YYYY-MM-DD') : '',
                        ngayKetThucNhanHoSo: currentData.ngayKetThucNhanHoSo ? moment(currentData.ngayKetThucNhanHoSo).format('YYYY-MM-DD') : '',
                        doiTuongEnum: initialDoiTuongEnum ?? doiTuongValue.quanVaDan,
                        chucDanhIds: currentData.chucDanhIds ? currentData.chucDanhIds.split(',').filter(x => x) : [],
                        thoiGianBacSi: currentData.thoiGianBacSi ?? '',
                        hocPhiBacSi: currentData.hocPhiBacSi ?? '',
                        thoiGianYSi: currentData.thoiGianYSi ?? '',
                        hocPhiYSi: currentData.hocPhiYSi ?? '',
                        thoiGianDieuDuong: currentData.thoiGianDieuDuong ?? '',
                        hocPhiDieuDuong: currentData.hocPhiDieuDuong ?? '',
                        thoiGianKTV: currentData.thoiGianKTV ?? '',
                        hocPhiKTV: currentData.hocPhiKTV ?? '',
                        thoiGianDinhDuongLamSang: currentData.thoiGianDinhDuongLamSang ?? '',
                        hocPhiDinhDuongLamSang: currentData.hocPhiDinhDuongLamSang ?? '',
                        trangThaiDotTuyenSinh: initialTrangThai ?? trangThaiDotTuyenSinhValue.sapMo,
                        coDoiTuongMienGiam: initialDoiTuongMienGiams.length > 0,
                        doiTuongMienGiams: initialDoiTuongMienGiams,
                        fileDinhKem: currentData.fileDinhKem ?? currentData.FileDinhKem ?? '',
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => {
                        const selectedChucDanhs = listDoiTuong
                            .filter(x => values.chucDanhIds && values.chucDanhIds.includes(x.id))
                            .map(x => (x.tenDoiTuong || '')
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .replace(/đ/g, 'd')
                                .replace(/Đ/g, 'D')
                                .trim()
                                .toLowerCase());

                        const isBacSi = selectedChucDanhs.includes('bac si');
                        const isYSi = selectedChucDanhs.includes('y si');
                        const isDieuDuong = selectedChucDanhs.includes('dieu duong');
                        const isKTV = selectedChucDanhs.includes('ky thuat vien');
                        const isDinhDuongLamSang = selectedChucDanhs.includes('dinh duong lam sang');

                        return (
                        <Form noValidate onSubmit={handleSubmit}>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Tên khóa học',
                                        name: 'tenKhoaHoc',
                                        component: (
                                            <>
                                                <Form.Control type="text" name="tenKhoaHoc" value={values.tenKhoaHoc} onChange={handleChange} />
                                                {errors.tenKhoaHoc && <div className="text-danger small mt-1">{errors.tenKhoaHoc}</div>}
                                            </>
                                        ),
                                        required: true
                                    },
                                    {
                                        label: 'Loại tuyển sinh',
                                        name: 'loaiTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="loaiTuyenSinh"
                                                value={values.loaiTuyenSinh}
                                                onChange={handleChange}
                                            >
                                                {objectLoaiTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.loaiTuyenSinh,
                                        required: true
                                    },
                                ]}
                            />
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="font-weight-bold">Nội dung khóa học</Form.Label>
                                        <Editor
                                            name="noiDungKhoaHoc"
                                            value={values.noiDungKhoaHoc}
                                            onChange={(val) => setFieldValue('noiDungKhoaHoc', val)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Ngày khai giảng',
                                        name: 'ngayKhaiGiang',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayKhaiGiang" value={values.ngayKhaiGiang} onChange={handleChange} />
                                                {errors.ngayKhaiGiang && <div className="text-danger small mt-1">{errors.ngayKhaiGiang}</div>}
                                            </>
                                        ),
                                        required: true
                                    },
                                    {
                                        label: 'Trạng thái',
                                        name: 'trangThaiDotTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="trangThaiDotTuyenSinh"
                                                value={values.trangThaiDotTuyenSinh}
                                                onChange={handleChange}
                                            >
                                                {objectTrangThaiDotTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.trangThaiDotTuyenSinh
                                    }
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Ngày nhận hồ sơ',
                                        name: 'ngayBatDauNhanHoSo',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayBatDauNhanHoSo" value={values.ngayBatDauNhanHoSo} onChange={handleChange} />
                                                {errors.ngayBatDauNhanHoSo && <div className="text-danger small mt-1">{errors.ngayBatDauNhanHoSo}</div>}
                                            </>
                                        )
                                    },
                                    {
                                        label: 'Ngày kết thúc nhận',
                                        name: 'ngayKetThucNhanHoSo',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayKetThucNhanHoSo" value={values.ngayKetThucNhanHoSo} onChange={handleChange} />
                                                {errors.ngayKetThucNhanHoSo && <div className="text-danger small mt-1">{errors.ngayKetThucNhanHoSo}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Đối tượng',
                                        name: 'doiTuongEnum',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="doiTuongEnum"
                                                value={values.doiTuongEnum}
                                                onChange={handleChange}
                                            >
                                                {objectDoiTuong.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.doiTuongEnum,
                                        required: true
                                    },
                                    {
                                        label: 'Chức danh',
                                        name: 'chucDanhIds',
                                        component: (
                                            <Select
                                                isMulti
                                                name="chucDanhIds"
                                                placeholder="-- Chọn Chức danh --"
                                                value={values.chucDanhIds ? values.chucDanhIds.map(v => {
                                                    const item = listDoiTuong.find(x => x.id === v);
                                                    return { label: item ? item.tenDoiTuong : '', value: v };
                                                }) : []}
                                                options={listDoiTuong.map(opt => ({ label: opt.tenDoiTuong, value: opt.id }))}
                                                onChange={(opts) => {
                                                    setFieldValue('chucDanhIds', opts ? opts.map(x => x.value) : []);
                                                }}
                                                styles={errors.chucDanhIds ? styleSelectError : styleSelectDefault}
                                            />
                                        ),
                                        error: errors.chucDanhIds
                                    },
                                ]}
                            />


                            {(isBacSi || isYSi || isDieuDuong || isKTV || isDinhDuongLamSang) && <h6 className="mt-4 mb-3">Học phí & Thời gian theo đối tượng</h6>}
                            {isBacSi && (
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Thời gian đào tạo Bác sĩ',
                                        name: 'thoiGianBacSi',
                                        component: (
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="thoiGianBacSi"
                                                    value={values.thoiGianBacSi}
                                                    onChange={handleChange}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <InputGroup.Text>tháng</InputGroup.Text>
                                            </InputGroup>
                                        ),
                                        error: errors.thoiGianBacSi,
                                    },
                                    {
                                        label: 'Học phí Bác sĩ',
                                        name: 'hocPhiBacSi',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="hocPhiBacSi" value={values.hocPhiBacSi} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.hocPhiBacSi && <div className="text-danger small mt-1">{errors.hocPhiBacSi}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            )}
                            {isYSi && (
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Thời gian đào tạo Y sĩ',
                                        name: 'thoiGianYSi',
                                        component: (
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="thoiGianYSi"
                                                    value={values.thoiGianYSi}
                                                    onChange={handleChange}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <InputGroup.Text>tháng</InputGroup.Text>
                                            </InputGroup>
                                        ),
                                        error: errors.thoiGianYSi,
                                    },
                                    {
                                        label: 'Học phí Y sĩ',
                                        name: 'hocPhiYSi',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="hocPhiYSi" value={values.hocPhiYSi} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.hocPhiYSi && <div className="text-danger small mt-1">{errors.hocPhiYSi}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            )}
                            {isDieuDuong && (
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Thời gian đào tạo Điều dưỡng',
                                        name: 'thoiGianDieuDuong',
                                        component: (
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="thoiGianDieuDuong"
                                                    value={values.thoiGianDieuDuong}
                                                    onChange={handleChange}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <InputGroup.Text>tháng</InputGroup.Text>
                                            </InputGroup>
                                        ),
                                        error: errors.thoiGianDieuDuong,
                                    },
                                    {
                                        label: 'Học phí Điều dưỡng',
                                        name: 'hocPhiDieuDuong',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="hocPhiDieuDuong" value={values.hocPhiDieuDuong} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.hocPhiDieuDuong && <div className="text-danger small mt-1">{errors.hocPhiDieuDuong}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            )}
                            {isKTV && (
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Thời gian đào tạo Kỹ thuật viên',
                                        name: 'thoiGianKTV',
                                        component: (
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="thoiGianKTV"
                                                    value={values.thoiGianKTV}
                                                    onChange={handleChange}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <InputGroup.Text>tháng</InputGroup.Text>
                                            </InputGroup>
                                        ),
                                        error: errors.thoiGianKTV,
                                    },
                                    {
                                        label: 'Học phí Kỹ thuật viên',
                                        name: 'hocPhiKTV',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="hocPhiKTV" value={values.hocPhiKTV} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.hocPhiKTV && <div className="text-danger small mt-1">{errors.hocPhiKTV}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            )}
                            {isDinhDuongLamSang && (
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Thời gian đào tạo Dinh dưỡng lâm sàng',
                                        name: 'thoiGianDinhDuongLamSang',
                                        component: (
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    name="thoiGianDinhDuongLamSang"
                                                    value={values.thoiGianDinhDuongLamSang}
                                                    onChange={handleChange}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <InputGroup.Text>tháng</InputGroup.Text>
                                            </InputGroup>
                                        ),
                                        error: errors.thoiGianDinhDuongLamSang,
                                    },
                                    {
                                        label: 'Học phí Dinh dưỡng lâm sàng',
                                        name: 'hocPhiDinhDuongLamSang',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="hocPhiDinhDuongLamSang" value={values.hocPhiDinhDuongLamSang} onChange={handleChange} onWheel={(e) => e.target.blur()} />
                                                {errors.hocPhiDinhDuongLamSang && <div className="text-danger small mt-1">{errors.hocPhiDinhDuongLamSang}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            )}

                            <Row className="mb-3">
                                <Col md={6}>
                                    <DoiTuongMienGiamFields values={values} errors={errors} setFieldValue={setFieldValue} />
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <DocumentInput
                                            labelTitle="File đính kèm"
                                            value={values.fileDinhKem}
                                            onChangeProp={(url) => setFieldValue('fileDinhKem', url)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group as={Row} className="mt-4 mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary">
                                            Cập nhật
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.LOPDAOTAOTHUCHANH_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                        );
                    }}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default LopDaoTaoThucHanhEdit;
